import React, { useState } from "react";
import { Alert, Platform, Pressable, Text, View } from "react-native";
import {
  getIncomeTypeLabel,
  incomeTypeRequiresPerson,
  INCOME_TYPE_OPTIONS,
  isWageIncomeType,
  type IncomeType,
} from "@perdiem/trpc/income-types";
import { capEmployeeRetirementContributions } from "@perdiem/trpc/wage-deductions";
import { trpc } from "../lib/trpc";
import { formatCurrency } from "../lib/utils";
import { Button } from "./Button";
import { useConfirmationDialog } from "./ConfirmationDialog";
import { FormErrorBanner } from "./FormErrorBanner";
import { Select } from "./Select";
import { CurrencyInput } from "./CurrencyInput";
import { TextInput } from "./TextInput";
import {
  endDateAfterYear,
  lastActiveYearFromEndDate,
  parseIncomeYear,
  startDateForYear,
  type PayFrequency,
} from "./IncomeStreamForm";
import { webPrivacyProps } from "../lib/privacy";


type DeductionSection = "pretax" | "taxes" | "aftertax" | "employer";

type DeductionFieldDef = {
  key: string;
  label: string;
  inputType: "currency" | "percent";
  section: DeductionSection;
  smartDefault?: "socialSecurity" | "medicare";
};

const DEDUCTION_SECTION_LABELS: Record<DeductionSection, string> = {
  pretax: "Pre-tax Deductions",
  taxes: "Tax Withholdings",
  aftertax: "After-tax Deductions",
  employer: "Employer-paid (not deducted)",
};

const DEDUCTION_FIELDS: DeductionFieldDef[] = [
  // Pre-tax deductions
  { key: "retirement401kPct", label: "Pre-tax Retirement Contributions", inputType: "percent", section: "pretax" },
  { key: "healthInsurance", label: "Health Insurance", inputType: "currency", section: "pretax" },
  { key: "dentalInsurance", label: "Dental", inputType: "currency", section: "pretax" },
  { key: "visionInsurance", label: "Vision", inputType: "currency", section: "pretax" },
  { key: "hsaContribution", label: "HSA", inputType: "currency", section: "pretax" },
  // Tax withholdings
  { key: "federalTax", label: "Federal Tax", inputType: "currency", section: "taxes" },
  { key: "stateTax", label: "State Tax", inputType: "currency", section: "taxes" },
  { key: "socialSecurityTax", label: "Social Security / OASDI", inputType: "currency", section: "taxes", smartDefault: "socialSecurity" },
  { key: "medicareTax", label: "Medicare", inputType: "currency", section: "taxes", smartDefault: "medicare" },
  // After-tax deductions
  { key: "roth401kPct", label: "Tax Deferred Retirement Contributions", inputType: "percent", section: "aftertax" },
  { key: "otherDeductions", label: "Other Deductions", inputType: "currency", section: "aftertax" },
  // Employer-paid
  { key: "employer401kMatchPct", label: "Employer Match", inputType: "percent", section: "employer" },
];

const PAY_FREQUENCY_OPTIONS: { value: PayFrequency; label: string }[] = [
  { value: "weekly", label: "Weekly (52/yr)" },
  { value: "biweekly", label: "Biweekly (26/yr)" },
  { value: "semimonthly", label: "Semi-monthly (24/yr)" },
  { value: "monthly", label: "Monthly (12/yr)" },
];

const PAY_FREQUENCY_MULTIPLIER: Record<PayFrequency, number> = {
  weekly: 52,
  biweekly: 26,
  semimonthly: 24,
  monthly: 12,
};

const PAY_FREQUENCY_LABELS: Record<PayFrequency, string> = {
  weekly: "Weekly",
  biweekly: "Biweekly",
  semimonthly: "Semi-monthly",
  monthly: "Monthly",
};

type IncomeStream = {
  id: string;
  name: string;
  type: string;
  annualAmount: number;
  growthRate: number;
  begin: string | null;
  end: string | null;
  personId: string | null;
  totalDeductions?: number;
  monthlyTakeHome?: number;
  annualTakeHome?: number;
  [key: string]: unknown;
};

type IncomeStreamListProfile = {
  id: string;
  type: "primary" | "spouse";
  firstName: string | null;
  lastName: string | null;
};

interface IncomeStreamListProps {
  streams: IncomeStream[];
  profiles?: IncomeStreamListProfile[];
  onMutated?: () => void | Promise<void>;
  readOnly?: boolean;
  compact?: boolean;
}

function getProfileLabel(
  profile: IncomeStreamListProfile,
  totalProfiles: number,
) {
  const name = [profile.firstName, profile.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (name) return name;
  if (totalProfiles === 1) return "You";
  return profile.type === "primary" ? "Primary" : "Spouse";
}

function EditTakeHomeSummary({
  annualGross,
  deductionStrings,
  payFrequency,
}: {
  annualGross: string;
  deductionStrings: Record<string, string>;
  payFrequency: PayFrequency;
}) {
  const gross = parseFloat(annualGross) || 0;
  if (gross <= 0) return null;
  const currentYear = new Date().getFullYear();
  const retirementPct = parseFloat(deductionStrings.retirement401kPct) || 0;
  const rothPct = parseFloat(deductionStrings.roth401kPct) || 0;
  const cappedRetirement = capEmployeeRetirementContributions(
    (retirementPct / 100) * gross,
    (rothPct / 100) * gross,
    currentYear,
  );

  // Sum annual deductions: currency fields are per-paycheck, % fields are of gross
  // Uses smart defaults for SS/Medicare when those fields are empty
  const SS_RATE = 0.062;
  const MEDICARE_RATE = 0.0145;
  const totalDeductions = DEDUCTION_FIELDS.reduce((sum, field) => {
    if (field.key === "employer401kMatchPct") return sum;
    if (field.inputType === "percent") {
      if (field.key === "retirement401kPct") return sum + cappedRetirement.employeePretax;
      if (field.key === "roth401kPct") return sum + cappedRetirement.employeeRoth;
    }
    // Use smart default when field is empty
    let val = parseFloat(deductionStrings[field.key]);
    if (Number.isNaN(val) || val <= 0) {
      if (field.smartDefault && gross > 0) {
        const rate = field.smartDefault === "socialSecurity" ? SS_RATE : MEDICARE_RATE;
        val = (gross * rate) / PAY_FREQUENCY_MULTIPLIER[payFrequency];
      } else {
        val = 0;
      }
    }
    return sum + val * PAY_FREQUENCY_MULTIPLIER[payFrequency];
  }, 0);
  const takeHome = gross - totalDeductions;
  const multiplier = PAY_FREQUENCY_MULTIPLIER[payFrequency];
  const grossPerPaycheck = gross / multiplier;
  const netPerPaycheck = takeHome / multiplier;
  const freqLabel = PAY_FREQUENCY_LABELS[payFrequency];

  return (
    <View className="rounded-lg bg-gray-50 dark:bg-gray-800 p-2 gap-1 mt-1">
      <View className="flex-row justify-between">
        <Text className="text-xs text-gray-500 dark:text-gray-400">Gross Per Paycheck ({freqLabel})</Text>
        <Text className="text-xs font-medium text-gray-700 dark:text-gray-300" {...webPrivacyProps('financial')}>
          {formatCurrency(grossPerPaycheck)}
        </Text>
      </View>
      <View className="flex-row justify-between">
        <Text className="text-xs text-gray-500 dark:text-gray-400">Deductions</Text>
        <Text className="text-xs font-medium text-red-600 dark:text-red-400" {...webPrivacyProps('financial')}>
          −{formatCurrency(totalDeductions / multiplier)}
        </Text>
      </View>
      <View className="border-t border-gray-200 dark:border-gray-700 mt-1 pt-1 flex-row justify-between">
        <Text className="text-xs font-semibold text-green-600 dark:text-green-400">
          Net Per Paycheck
        </Text>
        <Text className="text-xs font-semibold text-green-600 dark:text-green-400" {...webPrivacyProps('financial')}>
          {formatCurrency(netPerPaycheck)}
        </Text>
      </View>
      <View className="border-t border-gray-200 dark:border-gray-700 mt-1 pt-1 flex-row justify-between">
        <Text className="text-xs text-gray-500 dark:text-gray-400">Gross Annual</Text>
        <Text className="text-xs font-medium text-gray-700 dark:text-gray-300" {...webPrivacyProps('financial')}>
          {formatCurrency(gross)}
        </Text>
      </View>
      <View className="flex-row justify-between">
        <Text className="text-xs text-gray-500 dark:text-gray-400">Take-Home Annual</Text>
        <Text className="text-xs font-medium text-green-600 dark:text-green-400" {...webPrivacyProps('financial')}>
          {formatCurrency(takeHome)}
        </Text>
      </View>
    </View>
  );
}

export function IncomeStreamList({
  streams,
  profiles = [],
  onMutated,
  readOnly = false,
  compact = false,
}: IncomeStreamListProps) {
  const visibleProfiles = profiles.filter(
    (profile) => profile.type === "primary" || profile.type === "spouse",
  );
  const defaultWagePersonId =
    visibleProfiles.length === 1 ? visibleProfiles[0]!.id : null;
  const personLabels = new Map(
    visibleProfiles.map((profile) => [
      profile.id,
      getProfileLabel(profile, visibleProfiles.length),
    ]),
  );
  const personOptions = visibleProfiles.map((profile) => ({
    value: profile.id,
    label: personLabels.get(profile.id) ?? profile.id,
  }));

  const updateMutation = trpc.income.update.useMutation();
  const deleteMutation = trpc.income.delete.useMutation();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editAnnualAmount, setEditAnnualAmount] = useState("");
  const [editType, setEditType] = useState<IncomeType>("wages");
  const [editGrowthRate, setEditGrowthRate] = useState("");
  const [editBeginYear, setEditBeginYear] = useState("");
  const [editEndYear, setEditEndYear] = useState("");
  const [editPersonId, setEditPersonId] = useState<string | null>(
    defaultWagePersonId,
  );
  const [error, setError] = useState<string | null>(null);
  const [editDeductions, setEditDeductions] = useState<Record<string, string>>(
    () => Object.fromEntries(DEDUCTION_FIELDS.map(({ key }) => [key, ""])),
  );
  const [showEditDeductions, setShowEditDeductions] = useState(false);
  const [editPayFrequency, setEditPayFrequency] = useState<PayFrequency>("biweekly");
  const { confirmDestructive } = useConfirmationDialog();

  const currentYear = new Date().getFullYear();
  const isMutating = updateMutation.isPending || deleteMutation.isPending;

  const startEditing = (stream: IncomeStream) => {
    setError(null);
    setEditingId(stream.id);
    setEditName(stream.name);
    setEditAnnualAmount(
      String(Math.round(Number(stream.annualAmount) * 100) / 100),
    );
    setEditType(stream.type as IncomeType);
    setEditGrowthRate(
      String(Math.round(Number(stream.growthRate) * 100 * 10) / 10),
    );
    setEditBeginYear(
      stream.begin ? stream.begin.substring(0, 4) : "",
    );
    setEditEndYear(lastActiveYearFromEndDate(stream.end));
    setEditPersonId(stream.personId ?? defaultWagePersonId);
    // Populate deductions: currency fields monthly→per-paycheck, % fields raw
    const freq: PayFrequency = (stream.payFrequency as PayFrequency) || "biweekly";
    setEditPayFrequency(freq);
    const deductionVals: Record<string, string> = {};
    for (const { key, inputType } of DEDUCTION_FIELDS) {
      const val = Number(stream[key] ?? 0);
      if (inputType === "percent") {
        deductionVals[key] = val > 0 ? String(Math.round(val * 100) / 100) : "";
      } else {
        // monthly → per-paycheck
        const perPaycheck = (val * 12) / PAY_FREQUENCY_MULTIPLIER[freq];
        deductionVals[key] = perPaycheck > 0 ? String(Math.round(perPaycheck * 100) / 100) : "";
      }
    }
    setEditDeductions(deductionVals);
    setShowEditDeductions((stream.totalDeductions ?? 0) > 0);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName("");
    setEditAnnualAmount("");
    setEditType("wages");
    setEditGrowthRate("");
    setEditBeginYear("");
    setEditEndYear("");
    setEditPersonId(defaultWagePersonId);
    setError(null);
    setEditDeductions(
      Object.fromEntries(DEDUCTION_FIELDS.map(({ key }) => [key, ""])),
    );
    setShowEditDeductions(false);
    setEditPayFrequency("biweekly");
  };

  const handleSave = async (stream: IncomeStream) => {
    setError(null);
    const trimmedName = editName.trim();
    if (!trimmedName) {
      setError("Please enter a name for this income stream.");
      return;
    }

    const parsedAnnual = parseFloat(editAnnualAmount);
    if (!parsedAnnual || Number.isNaN(parsedAnnual) || parsedAnnual <= 0) {
      setError("Please enter a valid annual amount.");
      return;
    }

    const parsedGrowthRate = parseFloat(editGrowthRate);
    if (Number.isNaN(parsedGrowthRate) || parsedGrowthRate < 0 || parsedGrowthRate > 99) {
      setError("Annual growth rate must be between 0% and 99%.");
      return;
    }

    let beginDate: string | null = null;
    let endDate: string | null = null;
    try {
      const parsedBeginYear = parseIncomeYear(editBeginYear, "Begin");
      const parsedEndYear = parseIncomeYear(editEndYear, "End");
      if (
        parsedBeginYear !== null &&
        parsedEndYear !== null &&
        parsedEndYear < parsedBeginYear
      ) {
        setError("End year must be the same as or after begin year.");
        return;
      }
      beginDate = startDateForYear(parsedBeginYear);
      endDate = endDateAfterYear(parsedEndYear);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Enter valid income years.");
      return;
    }

    if (incomeTypeRequiresPerson(editType) && !editPersonId) {
      setError("Assign wage income to a person before saving.");
      return;
    }

    const nextPersonId = incomeTypeRequiresPerson(editType)
      ? editPersonId
      : isWageIncomeType(stream.type)
        ? null
        : undefined;

    // Parse deductions: currency fields annual→monthly, % fields pass through
    const deductionUpdates: Record<string, number> = {};
    if (isWageIncomeType(editType)) {
      for (const { key, label, inputType } of DEDUCTION_FIELDS) {
        const raw = (editDeductions[key] ?? "").trim();
        if (raw) {
          const val = parseFloat(raw);
          if (Number.isNaN(val) || val < 0) {
            setError(`Invalid value for ${label}.`);
            return;
          }
          // Per-paycheck → monthly; % fields pass through
          deductionUpdates[key] = inputType === "percent"
            ? val
            : (val * PAY_FREQUENCY_MULTIPLIER[editPayFrequency]) / 12;
        } else {
          deductionUpdates[key] = 0;
        }
      }
    } else {
      // Clear deductions when switching away from wages
      for (const { key } of DEDUCTION_FIELDS) {
        deductionUpdates[key] = 0;
      }
    }

    try {
      await updateMutation.mutateAsync({
        id: stream.id,
        name: trimmedName,
        amount: parsedAnnual / 12,
        type: editType,
        growthRate: parsedGrowthRate / 100,
        begin: beginDate,
        end: endDate,
        payFrequency: editPayFrequency,
        ...(nextPersonId !== undefined ? { personId: nextPersonId } : {}),
        ...deductionUpdates,
      });
      cancelEditing();
      await onMutated?.();
    } catch {
      setError("Could not update income stream. Please try again.");
    }
  };

  const handleDelete = async (streamId: string) => {
    setError(null);
    try {
      await deleteMutation.mutateAsync({ id: streamId });
      if (editingId === streamId) cancelEditing();
      await onMutated?.();
    } catch {
      setError("Could not delete income stream. Please try again.");
    }
  };

  const confirmDelete = async (streamId: string) => {
    const confirmed = await confirmDestructive({
      title: "Delete Income Stream",
      message: "Are you sure you want to delete this income stream? This cannot be undone.",
      confirmLabel: "Delete",
    });
    if (!confirmed) return;
    await handleDelete(streamId);
  };

  if (streams.length === 0) return null;

  return (
    <View>
      <FormErrorBanner error={error} onDismiss={() => setError(null)} />
      {error && <View className="mb-2" />}

      {streams.map((stream, i) => {
        const isEditing = editingId === stream.id;
        const monthly = Number(stream.annualAmount ?? 0) / 12;
        const annual = Number(stream.annualAmount ?? 0);
        const typeLabel = getIncomeTypeLabel(stream.type);
        const beginYear = stream.begin ? stream.begin.substring(0, 4) : null;
        const endYear = lastActiveYearFromEndDate(stream.end) || null;
        const isFuture = beginYear
          ? parseInt(beginYear, 10) > currentYear
          : false;
        const isLast = i === streams.length - 1;
        const personLabel = stream.personId
          ? (personLabels.get(stream.personId) ?? null)
          : null;
        const needsPersonAssignment =
          isWageIncomeType(stream.type) && !stream.personId;
        const subtitle =
          beginYear || endYear
            ? beginYear && endYear
              ? `${beginYear} – ${endYear}`
              : beginYear
                ? `from ${beginYear}`
                : `until ${endYear}`
            : stream.growthRate
              ? `${(Number(stream.growthRate) * 100).toFixed(1)}% annual growth`
              : null;

        return (
          <View
            key={stream.id}
            className={`py-3 ${isLast ? "" : "border-b border-gray-50 dark:border-gray-700"}`}
          >
            {isEditing ? (
              <View className="gap-3" onStartShouldSetResponder={() => true}>
                <View className="flex-row justify-between items-start">
                  <View className="flex-1 mr-3">
                    <Text
                      className="text-sm font-medium text-gray-800 dark:text-gray-200"
                      numberOfLines={1}
                      {...webPrivacyProps('private')}
                    >
                      {stream.name}
                    </Text>
                    <View className="flex-row items-center gap-2 mt-1">
                      <View
                        className={`rounded-full px-2 py-1 ${
                          isFuture
                            ? "bg-blue-50 dark:bg-blue-900/30"
                            : "bg-green-50 dark:bg-green-900/30"
                        }`}
                      >
                        <Text
                          className={`text-xs font-medium ${
                            isFuture
                              ? "text-blue-600 dark:text-blue-400"
                              : "text-green-600 dark:text-green-400"
                          }`}
                        >
                          {typeLabel}
                        </Text>
                      </View>
                      {subtitle && (
                        <Text className="text-xs text-gray-400 dark:text-gray-500">
                          {subtitle}
                        </Text>
                      )}
                    </View>
                    {needsPersonAssignment && (
                      <Text className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                        Assign to a person for FIRE timing
                      </Text>
                    )}
                    {!needsPersonAssignment && personLabel && (
                      <Text className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                        {personLabel}
                      </Text>
                    )}
                  </View>
                  {!compact && (
                    <View className="items-end">
                      <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200" {...webPrivacyProps('financial')}>
                        {formatCurrency(monthly)}/mo
                      </Text>
                      <Text className="text-xs text-gray-400 dark:text-gray-500" {...webPrivacyProps('financial')}>
                        {formatCurrency(annual)}/yr
                      </Text>
                    </View>
                  )}
                </View>

                <TextInput
                  label="Name / description"
                  value={editName}
                  onChangeText={(text) => {
                    setEditName(text);
                    setError(null);
                  }}
                  placeholder="e.g. Salary - Acme"
                  size="sm"
                />
                <CurrencyInput
                  label="Annual amount"
                  value={editAnnualAmount}
                  onChangeText={(text) => {
                    setEditAnnualAmount(text);
                    setError(null);
                  }}
                  size="sm"
                />
                <Select
                  label="Income type"
                  value={editType}
                  options={INCOME_TYPE_OPTIONS}
                  onChange={(value) => {
                    setEditType(value);
                    setError(null);
                    if (
                      incomeTypeRequiresPerson(value) &&
                      defaultWagePersonId
                    ) {
                      setEditPersonId(
                        (current) => current ?? defaultWagePersonId,
                      );
                    }
                  }}
                  size="sm"
                />
                <View className="gap-2">
                  <TextInput
                    label="Annual growth rate (%)"
                    value={editGrowthRate}
                    onChangeText={(text) => {
                      setEditGrowthRate(text);
                      setError(null);
                    }}
                    placeholder="e.g. 3"
                    keyboardType="decimal-pad"
                    size="sm"
                  />
                  <View className="flex-row gap-2">
                    <TextInput
                      label="Begin year"
                      value={editBeginYear}
                      onChangeText={(text) => {
                        setEditBeginYear(text);
                        setError(null);
                      }}
                      placeholder="e.g. 2028"
                      keyboardType="number-pad"
                      size="sm"
                      containerClassName="flex-1"
                    />
                    <TextInput
                      label="End year"
                      value={editEndYear}
                      onChangeText={(text) => {
                        setEditEndYear(text);
                        setError(null);
                      }}
                      placeholder="e.g. 2030"
                      keyboardType="number-pad"
                      size="sm"
                      containerClassName="flex-1"
                    />
                  </View>
                </View>
                {incomeTypeRequiresPerson(editType) && (
                  <Select
                    label="Person"
                    value={editPersonId}
                    options={personOptions}
                    onChange={(value) => {
                      setEditPersonId(value);
                      setError(null);
                    }}
                    placeholder="Select the person earning this wage income"
                    size="sm"
                  />
                )}

                {isWageIncomeType(editType) && (
                  <View>
                    <Pressable
                      onPress={() => setShowEditDeductions(!showEditDeductions)}
                      className="flex-row items-center justify-between py-2"
                    >
                      <Text className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        Payroll Deductions
                      </Text>
                      <Text className="text-xs text-green-600 dark:text-green-400">
                        {showEditDeductions ? "Hide" : "Edit deductions"}
                      </Text>
                    </Pressable>
                    {showEditDeductions && (
                      <View className="gap-2">
                        <Select
                          label="Pay frequency"
                          value={editPayFrequency}
                          options={PAY_FREQUENCY_OPTIONS}
                          onChange={(v) => setEditPayFrequency(v)}
                          size="sm"
                        />
                        <Text className="text-xs text-gray-500 dark:text-gray-400">
                          Enter per-paycheck amounts from your pay stub.
                        </Text>
                        {DEDUCTION_FIELDS.map(({ key, label, inputType, section, smartDefault }, i) => {
                          const prevSection = i > 0 ? DEDUCTION_FIELDS[i - 1]!.section : null;
                          const sectionHeader = section !== prevSection ? (
                            <Text key={`section-${section}`} className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mt-2">
                              {DEDUCTION_SECTION_LABELS[section]}
                            </Text>
                          ) : null;

                          const grossAnnual = parseFloat(editAnnualAmount) || 0;

                          if (inputType === "percent") {
                            const pctVal = parseFloat(editDeductions[key] ?? "") || 0;
                            const dollarAmt = pctVal > 0 && grossAnnual > 0
                              ? (pctVal / 100) * grossAnnual / PAY_FREQUENCY_MULTIPLIER[editPayFrequency]
                              : 0;
                            return (
                              <React.Fragment key={key}>
                                {sectionHeader}
                                <View className="flex-row items-end gap-2">
                                  <TextInput
                                    label={`${label} (%)`}
                                    value={editDeductions[key]}
                                    onChangeText={(text) => {
                                      setEditDeductions((prev) => ({ ...prev, [key]: text }));
                                      setError(null);
                                    }}
                                    placeholder="0"
                                    keyboardType="decimal-pad"
                                    size="sm"
                                    containerClassName="flex-1"
                                  />
                                  {dollarAmt > 0 && (
                                    <Text
                                      className="text-xs text-gray-400 dark:text-gray-500 pb-2.5"
                                      {...webPrivacyProps('financial')}
                                    >
                                      = {formatCurrency(dollarAmt)}/pay
                                    </Text>
                                  )}
                                </View>
                              </React.Fragment>
                            );
                          }
                          let placeholder = "0";
                          if (smartDefault && grossAnnual > 0) {
                            const rate = smartDefault === "socialSecurity" ? 0.062 : 0.0145;
                            const perPaycheck = (grossAnnual * rate) / PAY_FREQUENCY_MULTIPLIER[editPayFrequency];
                            const pctLabel = (rate * 100).toFixed(smartDefault === "medicare" ? 2 : 1);
                            placeholder = `$${Math.round(perPaycheck).toLocaleString()} (${pctLabel}%)`;
                          }
                          return (
                            <React.Fragment key={key}>
                              {sectionHeader}
                              <CurrencyInput
                                label={`${label} (per paycheck)`}
                                value={editDeductions[key]}
                                onChangeText={(text) => {
                                  setEditDeductions((prev) => ({ ...prev, [key]: text }));
                                  setError(null);
                                }}
                                onFocus={smartDefault ? () => {
                                  if (!editDeductions[key]?.trim() && grossAnnual > 0) {
                                    const rate = smartDefault === "socialSecurity" ? 0.062 : 0.0145;
                                    const defaultVal = Math.round(((grossAnnual * rate) / PAY_FREQUENCY_MULTIPLIER[editPayFrequency]) * 100) / 100;
                                    setEditDeductions((prev) => ({ ...prev, [key]: String(defaultVal) }));
                                  }
                                } : undefined}
                                placeholder={placeholder}
                                size="sm"
                              />
                            </React.Fragment>
                          );
                        })}
                        <EditTakeHomeSummary
                          annualGross={editAnnualAmount}
                          deductionStrings={editDeductions}
                          payFrequency={editPayFrequency}
                        />
                      </View>
                    )}
                  </View>
                )}

                <View className="flex-row gap-2 pt-1">
                  <Button
                    size="sm"
                    className="flex-1"
                    onPress={() => handleSave(stream)}
                    loading={updateMutation.isPending}
                    disabled={deleteMutation.isPending}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="flex-1"
                    onPress={cancelEditing}
                    disabled={isMutating}
                  >
                    Cancel
                  </Button>
                </View>

                {!readOnly && (
                  <View className="flex-row justify-end">
                    <Button
                      size="sm"
                      variant="destructive"
                      onPress={() => {
                        void confirmDelete(stream.id);
                      }}
                      disabled={isMutating}
                    >
                      Delete
                    </Button>
                  </View>
                )}
              </View>
            ) : (
              <Pressable
                onPress={() => startEditing(stream)}
                disabled={readOnly || isMutating || editingId !== null}
              >
                <View className="flex-row justify-between items-start">
                  <View className="flex-1 mr-3">
                    <Text
                      className="text-sm font-medium text-gray-800 dark:text-gray-200"
                      numberOfLines={1}
                      {...webPrivacyProps('private')}
                    >
                      {stream.name}
                    </Text>
                    <View className="flex-row items-center gap-2 mt-0.5">
                      <View
                        className={`rounded-full px-1.5 py-0.5 ${
                          isFuture
                            ? "bg-blue-50 dark:bg-blue-900/30"
                            : "bg-green-50 dark:bg-green-900/30"
                        }`}
                      >
                        <Text
                          className={`text-xs font-medium ${
                            isFuture
                              ? "text-blue-600 dark:text-blue-400"
                              : "text-green-600 dark:text-green-400"
                          }`}
                        >
                          {typeLabel}
                        </Text>
                      </View>
                      {subtitle && (
                        <Text className="text-xs text-gray-400 dark:text-gray-500">
                          {subtitle}
                        </Text>
                      )}
                    </View>
                    {needsPersonAssignment && (
                      <Text className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                        Assign to a person for FIRE timing
                      </Text>
                    )}
                    {!needsPersonAssignment && personLabel && (
                      <Text className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                        {personLabel}
                      </Text>
                    )}
                  </View>
                  <View className="items-end gap-1">
                    {(stream.totalDeductions ?? 0) > 0 ? (
                      <>
                        <Text className="text-sm font-semibold text-green-600 dark:text-green-400" {...webPrivacyProps('financial')}>
                          {formatCurrency(stream.monthlyTakeHome ?? monthly)}/mo
                        </Text>
                        <Text className="text-xs text-gray-400 dark:text-gray-500">
                          take-home
                        </Text>
                        {!compact && (
                          <Text className="text-xs text-gray-400 dark:text-gray-500" {...webPrivacyProps('financial')}>
                            {formatCurrency(monthly)}/mo gross
                          </Text>
                        )}
                      </>
                    ) : (
                      <>
                        <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200" {...webPrivacyProps('financial')}>
                          {formatCurrency(monthly)}/mo
                        </Text>
                        {!compact && (
                          <Text className="text-xs text-gray-400 dark:text-gray-500" {...webPrivacyProps('financial')}>
                            {formatCurrency(annual)}/yr
                          </Text>
                        )}
                      </>
                    )}
                  </View>
                </View>
              </Pressable>
            )}
          </View>
        );
      })}
    </View>
  );
}
